import { useState } from 'react';

import {
  Users,
  Rocket,
  Handshake,
  Lightbulb,
  Phone,
  MessageSquare,
  Zap,
  Heart,
  Wifi,
  MessageCircle,
  ArrowRight,
  Crown,
  Linkedin,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';

import v from "../assets/v.png";
import t from "../assets/t.jpeg";
import Seo from '../seo/Seo';
import { TEAM_SEO } from '../seo/pageSeo';

const TEAM = [
  {
    name: 'Vishal Rana',
    role: 'Founder · VTCodex',
    phone: '9729013924',
    linkedin: 'https://www.linkedin.com/in/vishalrana227',
    dp: v,
    bio: 'Vishal Rana is the Founder of VTCodex focused on building modern digital solutions, scalable systems and premium client experiences for businesses and institutions.',
    color: '#00f5ff',
    Icon: Crown,
  },
  {
    name: 'Tanisha Garg',
    role: 'Co-Founder · VTCodex',
    phone: '9306468015',
    linkedin: 'https://www.linkedin.com/in/tanisha-garg-70116b313',
    dp: t,
    bio: 'Tanisha Garg is the Co-Founder of VTCodex managing creative execution, user experience and modern web solutions with a strong focus on quality and innovation.',
    color: '#8b5cf6',
    Icon: Users,
  },
];

const DOTS = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 5 + 2,
  color: ['#00f5ff','#8b5cf6','#f472b6'][i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

const CULTURE = [
  {
    Icon: Rocket,
    title: 'Fast Execution',
    desc: 'Modern solutions delivered with speed, precision and attention to detail.',
  },
  {
    Icon: Handshake,
    title: 'Client First',
    desc: 'Every project is built around the client’s business goals and growth.',
  },
  {
    Icon: Lightbulb,
    title: 'Creative Innovation',
    desc: 'We focus on modern ideas, premium UI and futuristic digital experiences.',
  },
];

const CULTURE_PILLS = [
  { label: 'Premium Solutions', Icon: Zap, i: 0 },
  { label: 'Client Focused', Icon: Heart, i: 1 },
  { label: 'Creative Team', Icon: Lightbulb, i: 2 },
  { label: 'Remote Ready', Icon: Wifi, i: 0 },
  { label: 'Clear Communication', Icon: MessageCircle, i: 1 },
  { label: 'Modern Technology', Icon: Rocket, i: 2 },
];

function shuffleArray(arr) {
  const a = [...arr];

  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
}

export default function TeamPage() {

  const [shuffledTeam] = useState(() => shuffleArray(TEAM));

  return (
    <>
      <Seo {...TEAM_SEO} />
      {/* HERO */}
      <section
        className="relative w-full overflow-hidden flex items-center"
        style={{ minHeight:'92vh' }}
      >

        {/* background */}
        <div className="absolute inset-0">

          <div className="absolute inset-0 bg-[#050810]" />

          <div
            className="absolute -left-60 top-1/3 w-[650px] h-[650px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(0,245,255,.13) 0%,transparent 70%)',
              filter:'blur(55px)'
            }}
          />

          <div
            className="absolute -right-60 top-1/2 w-[600px] h-[600px] rounded-full"
            style={{
              background:'radial-gradient(circle,rgba(139,92,246,.15) 0%,transparent 70%)',
              filter:'blur(55px)'
            }}
          />

          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[900px] h-[350px]"
            style={{
              background:'radial-gradient(ellipse,rgba(244,114,182,.08) 0%,transparent 70%)',
              filter:'blur(40px)'
            }}
          />

          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:'linear-gradient(rgba(0,245,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,.03) 1px,transparent 1px)',
              backgroundSize:'50px 50px'
            }}
          />

          {DOTS.map(dot => (
            <span
              key={dot.id}
              className="absolute rounded-full"
              style={{
                left:`${dot.left}%`,
                top:`${dot.top}%`,
                width:`${dot.size}px`,
                height:`${dot.size}px`,
                background:dot.color,
                boxShadow:`0 0 ${dot.size * 4}px ${dot.color}`,
                animation:`dotPulse ${dot.dur}s ease-in-out ${dot.delay}s infinite alternate`,
                opacity:.6
              }}
            />
          ))}

        </div>

        {/* content */}
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20 text-center">

          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs tracking-widest mb-8"
          >
            <Users size={14} />
            THE TEAM BEHIND VTCODEX
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6"
            style={{
              fontSize:'clamp(2.8rem,7vw,5.5rem)'
            }}
          >
            Meet The People <br />
            <span className="grad-text">
              Building VTCodex
            </span>
          </h1>

          <p className="text-slate-400 max-w-3xl mx-auto leading-relaxed text-lg mb-12">
            VTCodex was founded by <strong className="text-white">Vishal Rana</strong> (Founder)
            and <strong className="text-white">Tanisha Garg</strong> (Co-Founder) – a focused leadership team
            building premium websites, Android apps, scalable systems and modern business solutions in India.
          </p>

          {/* avatars */}
          <div className="flex items-center justify-center gap-0 mb-8">

            {TEAM.map((m, i) => (
              <div
                key={i}
                className="w-24 h-24 rounded-full overflow-hidden -mx-4 border-4 transition-all duration-500 hover:scale-110"
                style={{
                  borderColor:m.color,
                  boxShadow:`0 0 25px ${m.color}55`,
                  zIndex:TEAM.length - i
                }}
              >
                <img
                  src={m.dp}
                  alt={`${m.name} – ${m.role}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}

          </div>

          <p className="text-slate-500 tracking-[6px] text-xs">
            VISHAL RANA · TANISHA GARG
          </p>

          {/* pills */}
          <div className="flex flex-wrap justify-center gap-3 mt-12">

            {CULTURE_PILLS.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm"
                style={{
                  borderColor:['rgba(0,245,255,.25)','rgba(139,92,246,.25)','rgba(244,114,182,.25)'][p.i],
                  background:['rgba(0,245,255,.06)','rgba(139,92,246,.06)','rgba(244,114,182,.06)'][p.i],
                  color:['#00f5ff','#8b5cf6','#f472b6'][p.i],
                }}
              >
                <p.Icon size={14} />
                {p.label}
              </div>
            ))}

          </div>

        </div>

      </section>

      {/* TEAM */}
      <section className="py-24 px-8 bg-[#0a0f1e]">

        <div className="max-w-6xl mx-auto">

          <SectionHeader
            label="OUR LEADERSHIP"
            title="Leadership Team"
            sub="Vishal Rana, Founder and Tanisha Garg, Co-Founder – the founders driving VTCodex forward."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">

            {shuffledTeam.map((m, i) => (
              <AnimatedCard
                key={i}
                delay={i * 0.1}
                className="group relative overflow-hidden rounded-[2rem]
                border border-white/10 bg-[#0d1428]
                transition-all duration-500 hover:-translate-y-3
                hover:shadow-[0_25px_60px_rgba(0,0,0,.65),0_0_35px_rgba(0,245,255,.18)]"
              >

                {/* glow */}
                <div
                  className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
                  style={{
                    background:`radial-gradient(circle,${m.color}25,transparent 70%)`,
                    transform:'translate(30%,-30%)'
                  }}
                />

                {/* image */}
                <div className="relative h-[420px] overflow-hidden">

                  <img
                    src={m.dp}
                    alt={`${m.name} – ${m.role}, VTCodex`}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />

                  <div
                    className="absolute inset-0"
                    style={{
                      background:'linear-gradient(to top,rgba(5,8,16,.95),transparent)'
                    }}
                  />

                </div>

                {/* content */}
                <div className="relative p-8 -mt-24 z-10">

                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                    style={{
                      background:`${m.color}15`,
                      border:`1px solid ${m.color}35`
                    }}
                  >
                    <m.Icon size={28} color={m.color} />
                  </div>

                  <h2 className="text-white text-3xl font-bold mb-2">
                    {m.name}
                  </h2>

                  <p
                    className="text-sm tracking-widest uppercase mb-5"
                    style={{
                      color:m.color
                    }}
                  >
                    {m.role}
                  </p>

                  <p className="text-slate-400 leading-relaxed mb-8">
                    {m.bio}
                  </p>

                  {/* buttons */}
                  <div className="flex gap-3 flex-wrap">

                    <a
                      href={`tel:+91${m.phone}`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold no-underline transition-all duration-300 hover:-translate-y-1"
                      style={{
                        background:`${m.color}12`,
                        border:`1px solid ${m.color}35`,
                        color:m.color
                      }}
                    >
                      <Phone size={16} />
                      Call
                    </a>

                    <a
                      href={`https://wa.me/91${m.phone}`}
                      target="#"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold no-underline transition-all duration-300 hover:-translate-y-1"
                      style={{
                        background:'rgba(37,211,102,.08)',
                        border:'1px solid rgba(37,211,102,.3)',
                        color:'#25d366'
                      }}
                    >
                      <MessageSquare size={16} />
                      WhatsApp
                    </a>

                    <a
                      href={m.linkedin}
                      target="#"
                      rel="me noopener noreferrer"
                      aria-label={`${m.name} on LinkedIn`}
                      title={`${m.name} – LinkedIn`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold no-underline transition-all duration-300 hover:-translate-y-1"
                      style={{
                        background:'rgba(10,102,194,.12)',
                        border:'1px solid rgba(10,102,194,.45)',
                        color:'#4aa3ff'
                      }}
                    >
                      <Linkedin size={16} />
                      LinkedIn
                    </a>

                  </div>

                </div>

              </AnimatedCard>
            ))}

          </div>

          {/* culture */}
          <div className="mt-24">

            <SectionHeader
              label="OUR VALUES"
              title="How We Work"
              sub="Modern thinking, premium execution and client-focused solutions."
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">

              {CULTURE.map((c, i) => (
                <AnimatedCard
                  key={i}
                  delay={i * 0.1}
                  className="rounded-[2rem] border border-white/10 bg-[#0d1428] p-8 text-center transition-all duration-500 hover:-translate-y-2"
                >

                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    style={{
                      background:['rgba(0,245,255,.1)','rgba(139,92,246,.1)','rgba(244,114,182,.1)'][i],
                      border:`1px solid ${['rgba(0,245,255,.3)','rgba(139,92,246,.3)','rgba(244,114,182,.3)'][i]}`
                    }}
                  >
                    <c.Icon
                      size={28}
                      color={['#00f5ff','#8b5cf6','#f472b6'][i]}
                    />
                  </div>

                  <h3 className="text-white text-2xl font-bold mb-4">
                    {c.title}
                  </h3>

                  <p className="text-slate-400 leading-relaxed">
                    {c.desc}
                  </p>

                </AnimatedCard>
              ))}

            </div>

          </div>

          {/* CTA */}
          <AnimatedCard
            delay={0.4}
            className="mt-20 rounded-[2rem] border border-cyan-400/10 bg-cyan-400/[0.04] p-12 text-center"
          >

            <h2 className="text-white text-5xl font-bold mb-5">
              Let’s Build Something Great
            </h2>

            <p className="text-slate-400 max-w-2xl mx-auto mb-10">
              We’re always excited to collaborate on modern websites,
              apps and premium digital experiences.
            </p>

            <div className="flex justify-center gap-4 flex-wrap">

              <a
                href="https://wa.me/919499130923"
                target="#"
                rel="noopener noreferrer"
                className="btn-primary no-underline"
              >
                <MessageSquare size={18} />
                Chat With Us
              </a>

              <a
                href="tel:+919499130923"
                className="btn-secondary no-underline"
              >
                <Phone size={18} />
                Call Now
              </a>

            </div>

          </AnimatedCard>

        </div>

      </section>

      <style>{`

        @keyframes dotPulse {
          from {
            opacity:.25;
            transform:scale(1);
          }
          to {
            opacity:.8;
            transform:scale(1.8);
          }
        }

      `}</style>
    </>
  );
}